import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AnalyticsDefinition, AnalyticsSummaryItem} from '../../models/vte';
import {AuthService} from '../../services/auth.service';
import {RoleService} from '../../services/role.service';
import {MetadataEntry} from '../../services/vte/vehicle-transaction-view.service';
import {
    OrganizationVisionSettingsForm,
    OrganizationVisionSettingsState,
    OrganizationVisionSettingsViewService,
    VisionModeOption,
} from '../../services/vte/organization-vision-settings-view.service';
import {OrganizationOption} from '../../services/vte/operations-organization.service';
import {VisionConfigService} from '../../services/vte/vision-config.service';
import {
    customerAnalyticsDescription,
    customerAnalyticsName,
    customerVisionModeLabel,
    customerVisionModeLifecycleHint,
    deriveVerificationProviderFromCapabilities,
    hasVisionAnalytics,
    implementationProviderLabel,
    isVehicleDetectionAnalyticsKey,
    verificationProviderLabel,
} from '../../services/vte/vision-customer-labels';
import {MetadataPanelComponent} from '../../shared/vte';

@Component({
    selector: 'app-organization-vision-settings',
    standalone: true,
    imports: [CommonModule, FormsModule, MetadataPanelComponent],
    templateUrl: './organization-vision-settings.component.html',
    styleUrl: './organization-vision-settings.component.css',
})
export class OrganizationVisionSettingsComponent implements OnInit {
    loading = false;
    saving = false;
    errorMessage = '';
    selectedOrgId = '';
    organizations: OrganizationOption[] = [];
    settingsState: OrganizationVisionSettingsState | null = null;
    form: OrganizationVisionSettingsForm = {
        vision_mode: 'disabled',
        vision_capabilities: [],
        vision_provider: '',
        enforce_threshold: 0.85,
        vision_failure_policy: 'fail_open',
        vision_timeout_seconds: 3,
    };
    private savedFormSnapshot = '';

    constructor(
        public viewService: OrganizationVisionSettingsViewService,
        private visionConfig: VisionConfigService,
        private auth: AuthService,
        private roleService: RoleService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        if (this.viewService.canFilterByOrganization()) {
            this.viewService.loadOrganizationOptions().subscribe({
                next: (organizations) => {
                    this.organizations = organizations;
                },
            });
            return;
        }
        this.loadSettings();
    }

    get canFilterByOrganization(): boolean {
        return this.viewService.canFilterByOrganization();
    }

    get canEdit(): boolean {
        const groups = this.auth.getCurrentUserInfo()?.role;
        return this.roleService.canEditVisionConfiguration(groups);
    }

    get showImplementationDetails(): boolean {
        return this.auth.isSuperUser();
    }

    get isReadOnly(): boolean {
        return !this.canEdit;
    }

    customerVisionModeLabel = customerVisionModeLabel;
    customerVisionModeLifecycleHint = customerVisionModeLifecycleHint;
    verificationProviderLabel = verificationProviderLabel;
    implementationProviderLabel = implementationProviderLabel;

    get hasVisionAnalyticsEnabled(): boolean {
        return hasVisionAnalytics(this.form.vision_capabilities || []);
    }

    get availableVisionModeOptions(): VisionModeOption[] {
        if (!this.hasVisionAnalyticsEnabled) {
            return this.viewService.visionModeOptions.filter((option) => option.value === 'disabled');
        }
        return this.viewService.visionModeOptions.filter((option) => option.value !== 'disabled');
    }

    get isEnforceThresholdEditable(): boolean {
        return this.form.vision_mode === 'enforced';
    }

    get canSaveOrganization(): boolean {
        if (!this.canEdit || !this.hasUnsavedChanges || this.saving) {
            return false;
        }
        if (this.canFilterByOrganization) {
            return !!this.selectedOrgId?.trim() && !!this.settingsState?.organizationId;
        }
        return !!this.settingsState?.organizationId;
    }

    get derivedVerificationProvider(): string {
        if (!this.settingsState) {
            return '';
        }
        return deriveVerificationProviderFromCapabilities(
            this.settingsState.availableDefinitions,
            this.form.vision_capabilities,
        );
    }

    get hasUnsavedChanges(): boolean {
        return this.serializeForm(this.form) !== this.savedFormSnapshot;
    }

    get visionModeOptions(): VisionModeOption[] {
        return this.availableVisionModeOptions;
    }

    get failurePolicyOptions() {
        return this.viewService.failurePolicyOptions;
    }

    /** OV-A3: camera-derived read-only summary (not vision_capabilities). */
    get analyticsSummary(): AnalyticsSummaryItem[] {
        return this.settingsState?.analyticsSummary || [];
    }

    /** Catalog-driven organization policy capabilities (editable; not camera CAC). */
    get vehicleDetectionDefinitions(): AnalyticsDefinition[] {
        return this.capabilityDefinitions.filter((definition) =>
            isVehicleDetectionAnalyticsKey(definition.analytics_key),
        );
    }

    get visionAnalyticsDefinitions(): AnalyticsDefinition[] {
        return this.capabilityDefinitions.filter(
            (definition) => !isVehicleDetectionAnalyticsKey(definition.analytics_key),
        );
    }

    private get capabilityDefinitions(): AnalyticsDefinition[] {
        const definitions = this.settingsState?.availableDefinitions || [];
        return this.viewService.getCustomerFacingDefinitions(definitions);
    }

    get featureFlagEntries(): MetadataEntry[] {
        if (!this.settingsState) {
            return [];
        }
        return Object.entries(this.settingsState.featureFlags).map(([key, value]) => ({
            key,
            label: key.replace(/_/g, ' '),
            value: value ? 'Enabled' : 'Disabled',
        }));
    }

    customerSummaryName(item: AnalyticsSummaryItem): string {
        return customerAnalyticsName(item);
    }

    customerDefinitionName(definition: AnalyticsDefinition): string {
        return customerAnalyticsName(definition);
    }

    customerDefinitionDescription(definition: AnalyticsDefinition): string | null {
        return customerAnalyticsDescription(definition);
    }

    isCapabilityEnabled(analyticsKey: string): boolean {
        return this.viewService.isCapabilityEnabledForCustomer(
            this.form.vision_capabilities || [],
            analyticsKey,
        );
    }

    onOrganizationChange(): void {
        this.loadSettings();
    }

    onVisionModeChange(): void {
        if (this.isReadOnly) {
            return;
        }
        const applied = this.viewService.applyVisionModeLifecycle(this.form, true);
        if (applied.error) {
            this.toastr.warning(applied.error, 'Vision Mode');
        }
        this.form = applied.form;
        this.onFormChange();
    }

    onVehicleDetectionChange(analyticsKey: string): void {
        this.applyCapabilityChange(analyticsKey, true);
    }

    onVisionAnalyticsChange(analyticsKey: string, enabled: boolean): void {
        this.applyCapabilityChange(analyticsKey, enabled);
    }

    onFormChange(): void {
        // Trigger change detection for unsaved-state banner and save button.
    }

    private applyCapabilityChange(analyticsKey: string, enabled: boolean): void {
        if (this.isReadOnly) {
            return;
        }
        const capabilities = this.viewService.toggleCapability(
            this.form.vision_capabilities || [],
            analyticsKey,
            enabled,
        );
        const applied = this.viewService.applyVisionModeLifecycle(
            {
                ...this.form,
                vision_capabilities: capabilities,
            },
            false,
        );
        this.form = applied.form;
        this.onFormChange();
    }

    resetForm(): void {
        if (!this.settingsState || this.saving) {
            return;
        }
        this.form = this.viewService.toForm(this.settingsState.policy);
        this.captureSavedSnapshot();
    }

    loadSettings(): void {
        if (this.canFilterByOrganization && !this.selectedOrgId?.trim()) {
            this.errorMessage = 'Select an organization to configure vision settings.';
            this.settingsState = null;
            this.loading = false;
            return;
        }

        const organizationId = this.viewService.resolveOrganizationId(this.selectedOrgId);
        if (!organizationId) {
            this.errorMessage = 'Select an organization to configure vision settings.';
            this.settingsState = null;
            this.loading = false;
            return;
        }

        this.loading = true;
        this.errorMessage = '';
        this.viewService.loadSettings(organizationId).subscribe({
            next: (state) => {
                this.settingsState = state;
                if (this.canFilterByOrganization) {
                    this.selectedOrgId = String(state.organizationId);
                }
                this.form = this.viewService.toForm(state.policy);
                this.captureSavedSnapshot();
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Unable to load organization vision settings.';
            },
        });
    }

    saveSettings(): void {
        if (!this.canEdit || !this.canSaveOrganization) {
            return;
        }
        const organizationId = this.settingsState?.organizationId
            ?? this.viewService.resolveOrganizationId(this.selectedOrgId);
        if (!organizationId) {
            this.toastr.error('Organization is required.', 'Vision Settings');
            return;
        }

        this.saving = true;
        const definitions = this.settingsState?.availableDefinitions || [];
        this.viewService.saveSettings(organizationId, this.form, definitions).subscribe({
            next: (policy) => {
                this.saving = false;
                if (this.canFilterByOrganization) {
                    this.selectedOrgId = String(organizationId);
                }
                const analyticsSummary = this.viewService.normalizeAnalyticsSummary(
                    policy.analytics_summary,
                );
                this.form = this.viewService.toForm(policy);
                this.captureSavedSnapshot();
                this.visionConfig.loadForOrganization(organizationId, true).subscribe({
                    next: (context) => {
                        if (this.settingsState) {
                            this.settingsState = {
                                ...this.settingsState,
                                policy: {
                                    ...policy,
                                    analytics_summary: analyticsSummary,
                                },
                                context,
                                availableDefinitions: context.analytics_definitions.filter(
                                    (definition) => definition.is_active,
                                ),
                                analyticsSummary,
                                featureFlags: {
                                    vte_pipeline_enabled: context.vte_pipeline_enabled !== false,
                                },
                            };
                        }
                    },
                });
                this.toastr.success('Organization vision settings saved.', 'Vision Settings');
            },
            error: (err) => {
                this.saving = false;
                const body = err?.error;
                const policy = body?.policy;
                if (policy) {
                    this.form = this.viewService.toForm(policy);
                    this.captureSavedSnapshot();
                    if (this.settingsState) {
                        this.settingsState = {
                            ...this.settingsState,
                            policy,
                            analyticsSummary: this.viewService.normalizeAnalyticsSummary(
                                policy.analytics_summary,
                            ),
                        };
                    }
                }
                const detail = typeof body?.detail === 'string' && body.detail.trim()
                    ? body.detail
                    : 'Unable to save organization vision settings.';
                this.errorMessage = detail;
                this.toastr.error(detail, 'Vision Settings');
            },
        });
    }

    private captureSavedSnapshot(): void {
        this.savedFormSnapshot = this.serializeForm(this.form);
    }

    private serializeForm(form: OrganizationVisionSettingsForm): string {
        return JSON.stringify({
            vision_mode: form.vision_mode,
            vision_capabilities: [...form.vision_capabilities].sort(),
            enforce_threshold: form.enforce_threshold,
            vision_failure_policy: form.vision_failure_policy,
            vision_timeout_seconds: form.vision_timeout_seconds,
        });
    }
}
